import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Plus, Trash2, Check, Share2, Printer, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getShoppingList, addShoppingItem, updateShoppingItem, deleteShoppingItem } from "@/lib/api";

export default function ShoppingListPage({ deviceId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    loadItems();
  }, [deviceId]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getShoppingList(deviceId);
      setItems(data);
    } catch (err) {
      toast.error("Could not load shopping list.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    try {
      const item = await addShoppingItem({
        device_id: deviceId,
        name: newItem.trim(),
        category: "manual",
      });
      setItems((prev) => [item, ...prev]);
      setNewItem("");
      setShowAdd(false);
      toast.success("Item added!");
    } catch (err) {
      toast.error("Could not add item.");
    }
  };

  const handleToggle = async (itemId, currentChecked) => {
    try {
      const updated = await updateShoppingItem(itemId, { checked: !currentChecked });
      setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
    } catch (err) {
      toast.error("Could not update item.");
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await deleteShoppingItem(itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (err) {
      toast.error("Could not remove item.");
    }
  };

  const handleClearChecked = async () => {
    const checked = items.filter((i) => i.checked);
    for (const item of checked) {
      try {
        await deleteShoppingItem(item.id);
      } catch {}
    }
    setItems((prev) => prev.filter((i) => !i.checked));
    toast.success("Cleared checked items");
  };

  const handleShare = async () => {
    const unchecked = items.filter((i) => !i.checked);
    const text = `My Garden Shopping List\n${"─".repeat(24)}\n${unchecked.map((i) => `- ${i.name}${i.quantity > 1 ? ` (x${i.quantity})` : ""}`).join("\n")}\n\nCreated with The Garden Guide`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: "Garden Shopping List", text });
      } catch {}
    } else {
      navigator.clipboard.writeText(text);
      toast.success("List copied to clipboard!");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const uncheckedItems = items.filter((i) => !i.checked);
  const checkedItems = items.filter((i) => i.checked);

  // Group by category
  const grouped = {};
  for (const item of uncheckedItems) {
    const cat = item.category || "general";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  return (
    <div className="px-5 pt-6 pb-4" data-testid="shopping-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="list-back-btn" className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white">
            <ArrowLeft size={24} strokeWidth={2.5} />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
              Shopping List
            </h1>
            <p className="text-base text-[#2A4D3B]">{uncheckedItems.length} items</p>
          </div>
        </div>
        <div className="flex gap-2 no-print">
          <button
            onClick={handleShare}
            data-testid="share-list-btn"
            className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white"
          >
            <Share2 size={20} strokeWidth={2.5} />
          </button>
          <button
            onClick={handlePrint}
            data-testid="print-list-btn"
            className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white"
          >
            <Printer size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Add Item */}
      {!showAdd ? (
        <Button
          onClick={() => setShowAdd(true)}
          data-testid="show-add-item-btn"
          className="w-full h-14 text-lg font-bold bg-[#0A2918] text-white rounded-xl press-active hard-shadow mb-4 no-print"
        >
          <Plus size={22} strokeWidth={2.5} className="mr-2" /> Add Item
        </Button>
      ) : (
        <div className="flex gap-2 mb-4 no-print animate-slide-up">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder="Item name..."
            autoFocus
            data-testid="new-item-input"
            className="flex-1 h-14 rounded-xl border-2 border-[#0A2918] px-4 text-lg font-medium bg-white text-[#0A2918] placeholder:text-[#2A4D3B]/50 focus:ring-4 focus:ring-[#FFA62B] focus:outline-none"
          />
          <Button
            onClick={handleAdd}
            data-testid="confirm-add-item-btn"
            className="h-14 px-5 text-lg font-bold bg-[#28593A] text-white rounded-xl press-active"
          >
            Add
          </Button>
          <button
            onClick={() => { setShowAdd(false); setNewItem(""); }}
            data-testid="cancel-add-item-btn"
            className="w-14 h-14 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active bg-white"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#28593A]" size={36} />
        </div>
      )}

      {/* Empty State */}
      {!loading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-list">
          <ShoppingCart size={56} strokeWidth={2} className="text-[#28593A]/40 mb-4" />
          <h3 className="text-xl font-bold text-[#0A2918] mb-2" style={{ fontFamily: 'Work Sans' }}>
            Your list is empty
          </h3>
          <p className="text-base text-[#2A4D3B] max-w-xs">
            Add items manually or identify plants to get product suggestions
          </p>
        </div>
      )}

      {/* Unchecked Items by Category */}
      {Object.entries(grouped).map(([category, categoryItems]) => (
        <div key={category} className="mb-4">
          {Object.keys(grouped).length > 1 && (
            <p className="text-sm font-bold text-[#2A4D3B] uppercase tracking-wider mb-2 px-1">{category}</p>
          )}
          <div className="space-y-2">
            {categoryItems.map((item, i) => (
              <div
                key={item.id}
                className="bg-white border-2 border-[#0A2918] rounded-xl p-4 flex items-center gap-3 hard-shadow-sm animate-slide-up"
                data-testid={`list-item-${item.id}`}
              >
                <button
                  onClick={() => handleToggle(item.id, item.checked)}
                  data-testid={`toggle-item-${item.id}`}
                  className="w-10 h-10 rounded-lg border-2 border-[#0A2918] flex items-center justify-center flex-shrink-0 press-active bg-white"
                >
                  {item.checked && <Check size={18} strokeWidth={3} className="text-[#28593A]" />}
                </button>
                <span className="flex-1 text-lg font-medium text-[#0A2918]">{item.name}</span>
                {item.quantity > 1 && (
                  <span className="text-sm font-bold bg-[#F2F7F4] px-2 py-1 rounded-lg text-[#2A4D3B]">x{item.quantity}</span>
                )}
                <button
                  onClick={() => handleDelete(item.id)}
                  data-testid={`delete-item-${item.id}`}
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-[#E25F45] press-active no-print"
                >
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Checked Items */}
      {checkedItems.length > 0 && (
        <div className="mt-6" data-testid="checked-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-[#2A4D3B] uppercase tracking-wider">Done ({checkedItems.length})</p>
            <button
              onClick={handleClearChecked}
              data-testid="clear-checked-btn"
              className="text-sm font-bold text-[#E25F45] press-active no-print"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2 opacity-60">
            {checkedItems.map((item) => (
              <div
                key={item.id}
                className="bg-[#F2F7F4] border border-[#0A2918]/20 rounded-xl p-4 flex items-center gap-3"
                data-testid={`checked-item-${item.id}`}
              >
                <button
                  onClick={() => handleToggle(item.id, item.checked)}
                  className="w-10 h-10 rounded-lg border-2 border-[#28593A] bg-[#28593A] flex items-center justify-center flex-shrink-0 press-active"
                >
                  <Check size={18} strokeWidth={3} className="text-white" />
                </button>
                <span className="flex-1 text-lg font-medium text-[#0A2918] line-through">{item.name}</span>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-[#E25F45] press-active no-print"
                >
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
